import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import img1 from '../../../assets/attendenceCarousel.png';
import { attendanceApi } from '../services/api';

export default function StudentAttendanceRegister() {
  const carouselImages = [img1];
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  // Get student ID from URL params
  const studentIdParam = params.get('studentid') || location.state?.student?.studentID || '';

  // Student profile state
  const [studentProfileState, setStudentProfileState] = useState<any>(null);
  const [studentLoading, setStudentLoading] = useState(true);
  const [studentError, setStudentError] = useState<string | null>(null);

  // Student section states
  const [guardianDetails, setGuardianDetails] = useState<any>(null);
  const [addressProfile, setAddressProfile] = useState<any>(null);
  const [firstAdmissionDetails, setFirstAdmissionDetails] = useState<any>(null);
  const [presentKVDetails, setPresentKVDetails] = useState<any>(null);
  const [currentAcademicDetails, setCurrentAcademicDetails] = useState<any>(null);
  const [previousSessionDetails, setPreviousSessionDetails] = useState<any>(null);

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    guardian: false,
    address: false,
    firstAdmission: false,
    presentKV: false,
    currentAcademic: false,
    previousSession: true
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Fetch student details
  useEffect(() => {
    const fetchStudentProfile = async () => {
      if (!studentIdParam) {
        setStudentError('No student ID provided');
        setStudentLoading(false);
        return;
      }

      setStudentLoading(true);
      setStudentError(null);

      try {
        // Fetch basic student details
        const response = await attendanceApi.getStudentDetails(studentIdParam);

        // Handle response structure - data might be wrapped in array or direct object
        const resp = Array.isArray(response?.data) ? response.data[0] : response?.data || response;

        if (resp) {
          // Blood group mapping
          const bloodGroupMap: Record<string, string> = {
            '1': 'A+', '2': 'A-', '3': 'B+', '4': 'B-',
            '5': 'AB+', '6': 'AB-', '7': 'O+', '8': 'O-'
          };

          // Mother tongue mapping (sample - extend as needed)
          const motherTongueMap: Record<string, string> = {
            '140': 'Hindi', '141': 'English', '142': 'Telugu', '143': 'Kannada',
            '144': 'Tamil', '145': 'Marathi', '146': 'Bengali', '147': 'Gujarati'
          };

          // Social category mapping
          const socialCategoryMap: Record<string, string> = {
            '1': 'General', '2': 'OBC', '3': 'SC', '4': 'ST', '11': 'General'
          };

          // Minority group mapping
          const minorityGroupMap: Record<string, string> = {
            '1': 'Christian', '2': 'Muslim', '3': 'Sikh', '4': 'Buddhist',
            '5': 'Parsi', '6': 'Jain', '7': 'Others'
          };

          const name = `${resp.firstName || ''} ${resp.middleName || ''} ${resp.lastName || ''}`.trim() || resp.firstName || 'N/A';
          const mappedStudent = {
            name,
            studentId: resp.studentID || resp.studentId || studentIdParam,
            uuid: resp.uuid || '-',
            academicSession: resp.academicSession || '-',
            gender: resp.gender === '1' ? 'Male' : resp.gender === '2' ? 'Female' : '-',
            dob: resp.dob ? new Date(resp.dob).toLocaleDateString() : '-',
            bloodGroup: bloodGroupMap[resp.bloodGroup] || resp.bloodGroup || '-',
            ubiId: resp.ubiID || '-',
            admissionNo: resp.admissionNo || '-',
            schoolName: resp.schoolName || '-',
            roName: resp.roName || '-',
            roID: resp.roID || '-',
            kvID: resp.kvID || '-',
            stateName: resp.stateName || '-',
            class: resp.class || resp['class'] || '-',
            section: resp.section || '-',
            stream: resp.stream || '-',
            rollNo: resp.rollNo || '-',
            apaarNo: resp.apaarNo || '-',
            admissionGround: resp.admissionGround || '-',
            admissionCategory: resp.admissionCategory || '-',
            parentServiceCategory: resp.parentServiceCategory || '-',
            motherTongue: motherTongueMap[resp.motherTongue] || resp.motherTongue || '-',
            socialCategory: socialCategoryMap[resp.socialCategory] || resp.socialCategory || '-',
            minorityGroup: minorityGroupMap[resp.minorityGroup] || resp.minorityGroup || '-',
            bplBeneficiary: resp.bplBeneficiary === '1' ? 'Yes' : resp.bplBeneficiary === '2' ? 'No' : '-',
            cwsn: resp.cwsnName || resp.cwsn === '99' ? 'Not Applicable' : resp.cwsn || '-',
            nationality: resp.nationality === '1' ? 'Indian' : resp.nationality || '-',
            sgc: resp.sgc === 'N' ? 'No' : resp.sgc === 'Y' ? 'Yes' : '-',
            status: resp.status === 1 ? 'Active' : resp.status === 0 ? 'Inactive' : '-',
            tcIssued: resp.tcIssued === '1' ? 'Yes' : resp.tcIssued === '0' ? 'No' : '-',
            upgraded: resp.upgraded === 1 ? 'Yes' : resp.upgraded === 0 ? 'No' : '-',
            photo: resp.photo || null,
            updatedAt: resp.updatedAt ? new Date(resp.updatedAt).toLocaleDateString() : '-',
          };
          setStudentProfileState(mappedStudent);
        }

        // Fetch all student section details in parallel
        const [guardian, address, firstAdmission, presentKV, currentAcademic, previousSession] = await Promise.all([
          attendanceApi.getStudentGuardianDetails(studentIdParam),
          attendanceApi.getStudentAddressProfile(studentIdParam),
          attendanceApi.getStudentFirstAdmissionDetails(studentIdParam),
          attendanceApi.getStudentPresentKVDetails(studentIdParam),
          attendanceApi.getStudentCurrentAcademicDetails(studentIdParam),
          attendanceApi.getStudentPreviousSessionDetails(studentIdParam),
        ]);

        if (guardian) setGuardianDetails(guardian);
        if (address) setAddressProfile(address);
        if (firstAdmission) setFirstAdmissionDetails(firstAdmission);
        if (presentKV) setPresentKVDetails(presentKV);
        if (currentAcademic) setCurrentAcademicDetails(currentAcademic);
        if (previousSession) setPreviousSessionDetails(previousSession);

      } catch (err) {
        console.error('Error fetching student details:', err);
        setStudentError(err instanceof Error ? err.message : 'Failed to load student details');
      } finally {
        setStudentLoading(false);
      }
    };

    fetchStudentProfile();
  }, [studentIdParam]);

  // Student profile photo URL
  const studentPhotoUrl =
    studentProfileState?.photo && studentProfileState.photo !== 'NA'
      ? `https://sis.kvs.gov.in/img/StudentImage/${studentProfileState.photo}`
      : null;

  return (
    <div className="min-h-screen flex flex-col gap-0 bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />
      <div className="w-full max-w-7xl px-4 pt-0 sm:pt-0">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-8xl mx-auto px-4 py-5 md:px-8 lg:px-16">
        <h2 className="text-2xl font-bold text-[#6A1786] mb-6">Student Attendance Register</h2>

        {studentLoading ? (
          // Loading State
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
              <p className="text-gray-600">Loading student details...</p>
            </div>
          </div>
        ) : studentError ? (
          // Error State
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600 font-semibold mb-2">Error Loading Student Data</p>
            <p className="text-red-500 text-sm">{studentError}</p>
            <p className="text-gray-600 text-sm mt-2">Student ID: {studentIdParam}</p>
          </div>
        ) : (
          // Student Profile Layout
          <div className="flex flex-col lg:flex-row gap-6 lg:items-stretch">
            {/* Left Side - Student Profile Card */}
            <div className="lg:w-1/3 flex flex-col gap-4">
              {/* Basic Profile */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start gap-4 mb-4">
                  {studentPhotoUrl ? (
                    <img
                      src={studentPhotoUrl}
                      alt={studentProfileState.name}
                      className="w-28 h-28 rounded-full object-cover border-4 border-yellow-400"
                    />
                  ) : (
                    <span className="w-28 h-28 rounded-full flex items-center justify-center bg-yellow-100 border-4 border-yellow-400 text-yellow-600 text-6xl">
                      <PersonIcon style={{ fontSize: 64 }} />
                    </span>
                  )}
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-[#632765]">{studentProfileState?.name}</h3>
                    <p className="text-xs text-gray-500 mt-1">Last Updated: {studentProfileState?.updatedAt}</p>
                    <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-2 text-sm">
                      <span className="text-gray-600">Gender</span>
                      <span className="text-gray-800 font-medium">{studentProfileState?.gender}</span>
                      <span className="text-gray-600">DOB</span>
                      <span className="text-gray-800 font-medium">{studentProfileState?.dob}</span>
                      <span className="text-gray-600">Class</span>
                      <span className="text-gray-800 font-medium">{studentProfileState?.class} - {studentProfileState?.section}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm border-t pt-4">
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">Student ID</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.studentId}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">UBI ID</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.ubiId}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">Admission No</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.admissionNo}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">Academic Session</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.academicSession}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">Roll No</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.rollNo}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">Blood Group</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.bloodGroup}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">APAAR No.</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.apaarNo}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">Mother Tongue</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.motherTongue}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">Social Category</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.socialCategory}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">Minority Group</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.minorityGroup}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">BPL Beneficiary</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.bplBeneficiary}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">CWSN</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.cwsn}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">Nationality</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.nationality}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">SGC</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.sgc}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">TC Issued</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.tcIssued}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">Status</span>
                    <span className={`font-medium ${studentProfileState?.status === 'Active' ? 'text-green-600' : 'text-red-600'}`}>{studentProfileState?.status}</span>
                  </div>
                </div>
              </div>

              {/* School Info Card */}
              <div className="bg-white rounded-lg shadow p-6">
                <h4 className="text-md font-semibold text-[#6A1786] mb-3">School Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="grid grid-cols-1 gap-1">
                    <span className="text-gray-600">School Name</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.schoolName}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">RO Name</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.roName}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">State</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.stateName}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">KV ID</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.kvID}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <span className="text-gray-600">RO ID</span>
                    <span className="text-gray-800 font-medium">{studentProfileState?.roID}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Collapsible Sections */}
            <div className="lg:w-2/3 flex flex-col gap-4">
              {/* Student Guardian Profile */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <button
                  onClick={() => toggleSection('guardian')}
                  className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-[#6A1786]">Student Guardian Profile {guardianDetails?.updatedAt ? `(Updated At ${new Date(guardianDetails.updatedAt).toLocaleDateString()})` : ''}</h3>
                  {expandedSections.guardian ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
                {expandedSections.guardian && (
                  <div className="p-4 border-t grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-600">Father Name:</span> <span className="font-medium">{guardianDetails?.fatherName || '-'}</span></div>
                    <div><span className="text-gray-600">Father Phone:</span> <span className="font-medium">{guardianDetails?.fatherMobileNo || '-'}</span></div>
                    <div><span className="text-gray-600">Father Email:</span> <span className="font-medium">{guardianDetails?.fatherEmailID || '-'}</span></div>
                    <div><span className="text-gray-600">Father Qualification:</span> <span className="font-medium">{guardianDetails?.fatherQualification || '-'}</span></div>
                    <div><span className="text-gray-600">Mother Name:</span> <span className="font-medium">{guardianDetails?.motherName || '-'}</span></div>
                    <div><span className="text-gray-600">Mother Phone:</span> <span className="font-medium">{guardianDetails?.motherMobileNo || '-'}</span></div>
                    <div><span className="text-gray-600">Mother Email:</span> <span className="font-medium">{guardianDetails?.motherEmailID || '-'}</span></div>
                    <div><span className="text-gray-600">Mother Qualification:</span> <span className="font-medium">{guardianDetails?.motherQualification || '-'}</span></div>
                    <div><span className="text-gray-600">Other Guardian Name:</span> <span className="font-medium">{guardianDetails?.otherName || '-'}</span></div>
                    <div><span className="text-gray-600">Other Guardian Phone:</span> <span className="font-medium">{guardianDetails?.otherMobileNo || '-'}</span></div>
                  </div>
                )}
              </div>

              {/* Address Profile */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <button
                  onClick={() => toggleSection('address')}
                  className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-[#6A1786]">Address Profile {addressProfile?.updatedAt ? `(Updated At ${new Date(addressProfile.updatedAt).toLocaleDateString()})` : ''}</h3>
                  {expandedSections.address ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
                {expandedSections.address && (
                  <div className="p-4 border-t space-y-3 text-sm">
                    <div className="font-semibold text-[#6A1786]">Current Address:</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><span className="text-gray-600">Address Line 1:</span> <span className="font-medium">{addressProfile?.current_add_1 || '-'}</span></div>
                      <div><span className="text-gray-600">Address Line 2:</span> <span className="font-medium">{addressProfile?.current_add_2 || '-'}</span></div>
                      <div className="col-span-2"><span className="text-gray-600">Address Line 3:</span> <span className="font-medium">{addressProfile?.current_add_3 || '-'}</span></div>
                      <div><span className="text-gray-600">City:</span> <span className="font-medium">{addressProfile?.current_city || '-'}</span></div>
                      <div><span className="text-gray-600">State:</span> <span className="font-medium">{addressProfile?.current_state_name || '-'}</span></div>
                      <div><span className="text-gray-600">PIN Code:</span> <span className="font-medium">{addressProfile?.current_pin || '-'}</span></div>
                    </div>
                    <div className="font-semibold text-[#6A1786] pt-2">Permanent Address:</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><span className="text-gray-600">Address Line 1:</span> <span className="font-medium">{addressProfile?.permanent_add_1 || '-'}</span></div>
                      <div><span className="text-gray-600">Address Line 2:</span> <span className="font-medium">{addressProfile?.permanent_add_2 || '-'}</span></div>
                      <div className="col-span-2"><span className="text-gray-600">Address Line 3:</span> <span className="font-medium">{addressProfile?.permanent_add_3 || '-'}</span></div>
                      <div><span className="text-gray-600">City:</span> <span className="font-medium">{addressProfile?.permanent_city || '-'}</span></div>
                      <div><span className="text-gray-600">State:</span> <span className="font-medium">{addressProfile?.permanent_state_name || '-'}</span></div>
                      <div><span className="text-gray-600">PIN Code:</span> <span className="font-medium">{addressProfile?.permanent_pin || '-'}</span></div>
                    </div>
                  </div>
                )}
              </div>

              {/* First Admission Details */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <button
                  onClick={() => toggleSection('firstAdmission')}
                  className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-[#6A1786]">First Admission Details {firstAdmissionDetails?.updatedAt ? `(Updated At ${new Date(firstAdmissionDetails.updatedAt).toLocaleDateString()})` : ''}</h3>
                  {expandedSections.firstAdmission ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
                {expandedSections.firstAdmission && (
                  <div className="p-4 border-t grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-600">Admission Date:</span> <span className="font-medium">{firstAdmissionDetails?.admissionDate ? new Date(firstAdmissionDetails.admissionDate).toLocaleDateString() : '-'}</span></div>
                    <div><span className="text-gray-600">Admission Year:</span> <span className="font-medium">{firstAdmissionDetails?.admissionYear || '-'}</span></div>
                    <div><span className="text-gray-600">First Admitted Class:</span> <span className="font-medium">{firstAdmissionDetails?.firstAdmitedclass || '-'}</span></div>
                    <div><span className="text-gray-600">Admission Category:</span> <span className="font-medium">{firstAdmissionDetails?.admissionCategory || '-'}</span></div>
                    <div><span className="text-gray-600">Admission Ground:</span> <span className="font-medium">{firstAdmissionDetails?.admissionGround || '-'}</span></div>
                    <div><span className="text-gray-600">RO ID:</span> <span className="font-medium">{firstAdmissionDetails?.roID || '-'}</span></div>
                    <div><span className="text-gray-600">KV ID:</span> <span className="font-medium">{firstAdmissionDetails?.kvID || '-'}</span></div>
                  </div>
                )}
              </div>

              {/* Present KV details */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <button
                  onClick={() => toggleSection('presentKV')}
                  className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-[#6A1786]">Present KV Details</h3>
                  {expandedSections.presentKV ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
                {expandedSections.presentKV && (
                  <div className="p-4 border-t grid grid-cols-2 gap-3 text-sm">
                    <div className="col-span-2"><span className="text-gray-600">School Name:</span> <span className="font-medium">{presentKVDetails?.schoolName || '-'}</span></div>
                    <div><span className="text-gray-600">RO Name:</span> <span className="font-medium">{presentKVDetails?.roName || '-'}</span></div>
                    <div><span className="text-gray-600">State Name:</span> <span className="font-medium">{presentKVDetails?.stateName || '-'}</span></div>
                    <div><span className="text-gray-600">Class:</span> <span className="font-medium">{presentKVDetails?.class || '-'}</span></div>
                    <div><span className="text-gray-600">Section:</span> <span className="font-medium">{presentKVDetails?.section || '-'}</span></div>
                    <div><span className="text-gray-600">Roll No:</span> <span className="font-medium">{presentKVDetails?.rollNo || '-'}</span></div>
                    <div><span className="text-gray-600">Stream:</span> <span className="font-medium">{presentKVDetails?.stream || '-'}</span></div>
                    <div><span className="text-gray-600">Admission No:</span> <span className="font-medium">{presentKVDetails?.admissionNo || '-'}</span></div>
                    <div><span className="text-gray-600">Admission Ground:</span> <span className="font-medium">{presentKVDetails?.admissionGround || '-'}</span></div>
                    <div><span className="text-gray-600">Admission Category:</span> <span className="font-medium">{presentKVDetails?.admissionCategory || '-'}</span></div>
                    <div><span className="text-gray-600">Parent Service Category:</span> <span className="font-medium">{presentKVDetails?.parentServiceCategory || '-'}</span></div>
                    <div><span className="text-gray-600">DOB:</span> <span className="font-medium">{presentKVDetails?.dob || '-'}</span></div>
                    <div><span className="text-gray-600">Gender:</span> <span className="font-medium">{presentKVDetails?.gender === '1' ? 'Male' : presentKVDetails?.gender === '2' ? 'Female' : '-'}</span></div>
                    <div><span className="text-gray-600">Blood Group:</span> <span className="font-medium">{presentKVDetails?.bloodGroup || '-'}</span></div>
                    <div><span className="text-gray-600">Nationality:</span> <span className="font-medium">{presentKVDetails?.nationality === '1' ? 'Indian' : presentKVDetails?.nationality || '-'}</span></div>
                    <div><span className="text-gray-600">Mother Tongue:</span> <span className="font-medium">{presentKVDetails?.motherTongue || '-'}</span></div>
                    <div><span className="text-gray-600">Social Category:</span> <span className="font-medium">{presentKVDetails?.socialCategory || '-'}</span></div>
                    <div><span className="text-gray-600">Minority Group:</span> <span className="font-medium">{presentKVDetails?.minorityGroup || '-'}</span></div>
                    <div><span className="text-gray-600">BPL Beneficiary:</span> <span className="font-medium">{presentKVDetails?.bplBeneficiary === '1' ? 'Yes' : presentKVDetails?.bplBeneficiary === '2' ? 'No' : '-'}</span></div>
                    <div><span className="text-gray-600">CWSN:</span> <span className="font-medium">{presentKVDetails?.cwsn || '-'}</span></div>
                    <div><span className="text-gray-600">SGC:</span> <span className="font-medium">{presentKVDetails?.sgc || '-'}</span></div>
                  </div>
                )}
              </div>

              {/* Current Academic Session Details */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <button
                  onClick={() => toggleSection('currentAcademic')}
                  className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-[#6A1786]">Current Academic Session Details {currentAcademicDetails?.updatedAt ? `(Updated At ${new Date(currentAcademicDetails.updatedAt).toLocaleDateString()})` : ''}</h3>
                  {expandedSections.currentAcademic ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
                {expandedSections.currentAcademic && (
                  <div className="p-4 border-t grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-600">Academic Session:</span> <span className="font-medium">{currentAcademicDetails?.academicSession || '-'}</span></div>
                    <div><span className="text-gray-600">Class:</span> <span className="font-medium">{currentAcademicDetails?.class || '-'}</span></div>
                    <div><span className="text-gray-600">Section:</span> <span className="font-medium">{currentAcademicDetails?.section || '-'}</span></div>
                    <div><span className="text-gray-600">Stream:</span> <span className="font-medium">{currentAcademicDetails?.stream || '-'}</span></div>
                    <div><span className="text-gray-600">Roll No:</span> <span className="font-medium">{currentAcademicDetails?.rollNo || '-'}</span></div>
                    <div><span className="text-gray-600">Subject:</span> <span className="font-medium">{currentAcademicDetails?.subject || '-'}</span></div>
                    <div className="col-span-2"><span className="text-gray-600">School Name:</span> <span className="font-medium">{currentAcademicDetails?.schoolName || '-'}</span></div>
                    <div><span className="text-gray-600">RO Name:</span> <span className="font-medium">{currentAcademicDetails?.roName || '-'}</span></div>
                    <div><span className="text-gray-600">State Name:</span> <span className="font-medium">{currentAcademicDetails?.stateName || '-'}</span></div>
                    <div><span className="text-gray-600">RO ID:</span> <span className="font-medium">{currentAcademicDetails?.roID || '-'}</span></div>
                    <div><span className="text-gray-600">KV ID:</span> <span className="font-medium">{currentAcademicDetails?.kvID || '-'}</span></div>
                    <div><span className="text-gray-600">UBI ID:</span> <span className="font-medium">{currentAcademicDetails?.ubiID || '-'}</span></div>
                    <div><span className="text-gray-600">Status:</span> <span className={`font-medium ${currentAcademicDetails?.status === 1 ? 'text-green-600' : 'text-red-600'}`}>{currentAcademicDetails?.status === 1 ? 'Active' : currentAcademicDetails?.status === 0 ? 'Inactive' : '-'}</span></div>
                  </div>
                )}
              </div>

              {/* Previous Session Details */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <button
                  onClick={() => toggleSection('previousSession')}
                  className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-[#6A1786]">Previous Session Details {previousSessionDetails?.updatedAt ? `(Updated At ${new Date(previousSessionDetails.updatedAt).toLocaleDateString()})` : ''}</h3>
                  {expandedSections.previousSession ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
                {expandedSections.previousSession && (
                  <div className="p-4 border-t">
                    <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                      <div><span className="text-gray-600">Academic Session:</span> <span className="font-medium">{previousSessionDetails?.academicSession || '-'}</span></div>
                      <div><span className="text-gray-600">Class:</span> <span className="font-medium">{previousSessionDetails?.class || '-'}</span></div>
                      <div><span className="text-gray-600">Section:</span> <span className="font-medium">{previousSessionDetails?.section || '-'}</span></div>
                      <div><span className="text-gray-600">Stream:</span> <span className="font-medium">{previousSessionDetails?.stream || '-'}</span></div>
                      <div><span className="text-gray-600">Roll No:</span> <span className="font-medium">{previousSessionDetails?.rollNo || '-'}</span></div>
                      <div><span className="text-gray-600">CBSE Roll No:</span> <span className="font-medium">{previousSessionDetails?.cbseRollNo || '-'}</span></div>
                      <div className="col-span-2"><span className="text-gray-600">School Name:</span> <span className="font-medium">{previousSessionDetails?.schoolName || '-'}</span></div>
                      <div><span className="text-gray-600">RO Name:</span> <span className="font-medium">{previousSessionDetails?.roName || '-'}</span></div>
                      <div><span className="text-gray-600">State Name:</span> <span className="font-medium">{previousSessionDetails?.stateName || '-'}</span></div>
                      <div><span className="text-gray-600">RO ID:</span> <span className="font-medium">{previousSessionDetails?.roID || '-'}</span></div>
                      <div><span className="text-gray-600">KV ID:</span> <span className="font-medium">{previousSessionDetails?.kvID || '-'}</span></div>
                      <div><span className="text-gray-600">Subject:</span> <span className="font-medium">{previousSessionDetails?.subject || '-'}</span></div>
                      <div><span className="text-gray-600">Status:</span> <span className={`font-medium ${previousSessionDetails?.status === 1 ? 'text-green-600' : 'text-red-600'}`}>{previousSessionDetails?.status === 1 ? 'Active' : previousSessionDetails?.status === 0 ? 'Inactive' : '-'}</span></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
